import { Channel, type ChannelEvents } from '@anycable/web';

/**
 * A single live technician-location frame as the backend `AppointmentsChannel`
 * broadcasts it (`tenants_channel.rb#broadcast_to_appointments`, payload sliced
 * to `technicianId,routeMetadata,position`).
 *
 * `technicianId` keys the multi-tech demux (TRACK-03); it equals
 * `AppointmentTechnician.id` (merged server-side), so the position Map and the
 * color Map share an id space.
 *
 * `routeMetadata.polyline` is a Google **encoded polyline string** (decode via
 * `google.maps.geometry.encoding.decodePath`), present only when the tech has a
 * stored route. `routeMetadata.eta` is the route ETA caption when present.
 */
export interface TechnicianLocationPayload {
  technicianId: number;
  position: {
    latitude: number;
    longitude: number;
    created_at?: string;
    /** Ground speed in METERS PER SECOND (device GPS, W3C standard). */
    speed?: number | null;
    /** Heading in degrees clockwise from true north (0–359), when known. */
    heading?: number | null;
  };
  routeMetadata?: {
    eta?: string;
    polyline?: string;
  };
}

/**
 * One technician's tracking record as the backend `AppointmentsChannel`
 * serializes it in the initial `tech_tracking_state` snapshot (transmitted on
 * subscribe — `appointments_channel.rb#transmit_initial_tracking_state`).
 *
 * The stream is already per-appointment (`stream_for(appointment)`), so the
 * record carries NO `appointmentId` of its own — every record is for THIS
 * appointment. `technicianId` keys the multi-tech demux (TRACK-03); it equals
 * `AppointmentTechnician.id` (merged server-side), so the position Map and the
 * color Map share an id space.
 *
 * `routePolyline` is a Google **encoded polyline string** (decode via
 * `google.maps.geometry.encoding.decodePath`). It is present only when the tech
 * has a stored route; `null` otherwise. `position` is `null` when there is no
 * last-known GPS fix.
 */
export interface TechTrackingRecord {
  technicianId: number;
  travelStatus?: string;
  position: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp?: number;
    created_at?: string;
    /** Ground speed in METERS PER SECOND, when the snapshot carries it. */
    speed?: number | null;
    heading?: number | null;
  } | null;
  routePolyline: string | null;
}

/**
 * The snapshot the server transmits immediately on subscribe — the current
 * tracking state of every technician on THIS appointment. The `appointmentId`
 * echoes the subscribed appointment; it is informational only (the stream is
 * already scoped, so consumers seed every record without filtering).
 */
export interface TechTrackingStateMessage {
  action: 'tech_tracking_state';
  appointmentId: number;
  technicians: TechTrackingRecord[];
}

/** Any frame the channel re-dispatches: a snapshot, a location ping, or anything else. */
export type AppointmentsChannelMessage =
  | TechTrackingStateMessage
  | TechnicianLocationPayload
  | { action?: string; position?: unknown; [key: string]: unknown };

/** The per-appointment subscription param the server `stream_for(appointment)` keys on. */
export type AppointmentsChannelParams = {
  id: number | string;
} & Record<string, string | number | boolean>;

// Extend the built-in channel events with our snapshot + live-location events.
interface AppointmentsEvents extends ChannelEvents<AppointmentsChannelMessage> {
  'tech-tracking-state': (payload: TechTrackingStateMessage) => void;
  'technician-location': (payload: TechnicianLocationPayload) => void;
}

/**
 * The single, proven per-appointment realtime channel
 * (`api/app/channels/appointments_channel.rb`). It is IDENTITY-INDEPENDENT:
 * the server `subscribed` does `stream_for(appointment)` keyed on the `{ id }`
 * param (rejecting if the appointment is not in `active_customer_appointments`),
 * so this stream is already scoped to one appointment and needs no client-side
 * filter.
 *
 * It owns BOTH paths now that `Customers::Channel` is removed:
 *   • on subscribe the server `transmit`s a `tech_tracking_state` SNAPSHOT —
 *     re-emitted here as `tech-tracking-state`;
 *   • thereafter each broadcast carries a `position` (a LIVE ping) — re-emitted
 *     as `technician-location`.
 * Any other frame falls through to the AnyCable base.
 */
export class AppointmentsChannel extends Channel<
  AppointmentsChannelParams,
  AppointmentsChannelMessage,
  AppointmentsEvents
> {
  static identifier = 'AppointmentsChannel';

  constructor(params: AppointmentsChannelParams) {
    super(params);
  }

  receive(message: AppointmentsChannelMessage) {
    // Read the discriminants off a loose view: the union members do not all
    // carry `action`/`position`, so narrow on a structural probe instead.
    const frame = message as { action?: unknown; position?: unknown };
    if (frame?.action === 'tech_tracking_state') {
      this.emit('tech-tracking-state', message as TechTrackingStateMessage);
      return;
    }
    if (frame?.position) {
      this.emit('technician-location', message as TechnicianLocationPayload);
      return;
    }
    super.receive(message);
  }
}
